import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden - the light is the buffer's own history
// (see echo.js), and here even its colour is a table, not a light.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'flowmap',
  family: 'shader',
  danceability: 0.80,
  requiresTier: 1,
  energyLevel: 0.76,
  bpmMin: 110,
  bpmMax: 150,
  movement: 0.80,
  tonality: 0.60,
  enabled: true
};

// Flowmap: a GREYSCALE intensity buffer, advected by analytic flow fields,
// into which waveforms are injected - and ALL of whose colour comes from a
// 256-entry colour table that scrolls. The reference is G-Force (Andy
// O'Meara): flow fields, wave shapes and colour maps, combined
// independently.
//
// THE DECISIVE DETAIL - colour is separate from form. The feedback loop
// stores one intensity (the R channel). The display reads it through the
// table: colour = table(fract(intensity * stretch + offset)) for a cyclic
// table, table(clamp(...)) otherwise. So a SHIFT of the table makes the
// colours flow through the whole picture while nothing moves (the G-Force
// effect); changing the table repaints the whole screen in one frame, on a
// drop; and the loop has a single channel to keep stable. Preset colours
// inside its buffer; this never does.
//
// Infrastructure: Echo's FeedbackBuffer, its three stability guards (decay
// below 1, a floor subtraction, a ceiling), its luminance governor and its
// signal-masked dither - read echo.js's header for why each exists.

// --- the loop (echo.js) -------------------------------------------------------
const DECAY_DEFAULT = 0.965;     // longer trails than Echo's 0.955; the governor caps the mean
const DECAY_MIN = 0.85;
const DECAY_MAX = 0.975;
const FLOOR_SUB = 0.004;
const CEILING = 0.92;
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.35;
const GOVERN_RESUME = 0.25;
const GOVERN_LOW = 0.05;
const GOVERN_STEP = 0.01;
const LUM_SIZE = 8;
const FADE_DECAY = 0.96;
const TRACK_FADE_S = 1.2;

// --- the fields -----------------------------------------------------------------
export const FIELDS = ['vortex', 'sink', 'source', 'twin', 'shear', 'ripple', 'curl', 'grid', 'spiral', 'lens'];
const FIELD_ID = {}; FIELDS.forEach((n, i) => { FIELD_ID[n] = i; });
const FIELD_TRANSITION_BEATS = 2;
const SPEED_BASE = 0.22;          // uv per second, times the energy
const BAR_PULSE_GAIN = 2.0;       // speed x2 for a beat
const DROP_SOURCE_GAIN = 3.0;     // a strong SOURCE for a bar

// --- the waves (preset.js) ---------------------------------------------------------
export const WAVES = ['line', 'circle', 'rose', 'spiral', 'lissajous', 'dots'];
const WAVE_POINTS = 256;
const WAVE_HALO = 2.2;
const WAVE_GAIN_MIN = 0.7, WAVE_GAIN_MAX = 1.0;
const WAVE_AMP = 0.10;
const BAND_CENTRE = [0.18, 0.5, 0.82];
const BAND_SIGMA = [0.16, 0.14, 0.12];
const DECORRELATION = 0.10;
const ATTACK_TAU = 0.03, RELEASE_TAU = 0.18;
const TEMPO_WOBBLE = 0.35;
const DOT_PERIOD = 0.0625;
const BEAT_BOOST_S = 0.12;
const BAND_TAU = 0.15;

// --- the colour tables ---------------------------------------------------------
const MAP_SIZE = 256;
const OFFSET_STEP = 1 / 16;       // a notch of the table, on the beat
const OFFSET_DRIFT = 0.012;       // turns per second, continuous
const OFFSET_RISE_MAX = 0.18;     // turns per second at the top of a rise
const STRETCH_MIN = 1.0, STRETCH_MAX = 3.0;   // bands.high: more contours (cyclic tables)
// A non-cyclic table is CLAMPED: stretched by 3 every intensity past a third
// read its warm-white end and the frame went white (Pi bench: mean 211/255).
// So those tables stretch little and breathe little.
const PLAIN_STRETCH_MIN = 0.85, PLAIN_STRETCH_MAX = 1.25, PLAIN_BREATH = 0.08;
const DROP_INVERT_FRAMES = 2;
const PAUSE_DRIFT = 0.004;        // the table keeps scrolling, slowly, while frozen
const SAFE_FLOOR = 0.5;

const TIER = {
  // Tier 0 is a live-downgrade refuge only (requiresTier 1).
  0: { scale: 0.25, fields: FIELDS.filter((f) => f !== 'curl') },
  1: { scale: 0.25, fields: FIELDS.filter((f) => f !== 'curl') },
  2: { scale: 0.5, fields: FIELDS.slice() }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(t) { return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}

// --- the tables, pure: 256 RGB entries. Exported for the tests.
// A gradient through `stops` ([t, [r,g,b]] with t in 0..1), repeated `cycles`
// times for a cyclic table.
function gradient(stops, cycles) {
  const out = new Uint8Array(MAP_SIZE * 4);
  for (let i = 0; i < MAP_SIZE; i++) {
    let t = i / (MAP_SIZE - 1);
    if (cycles > 1) t = (t * cycles) % 1;
    let a = stops[0], b = stops[stops.length - 1];
    for (let k = 0; k < stops.length - 1; k++) if (t >= stops[k][0] && t <= stops[k + 1][0]) { a = stops[k]; b = stops[k + 1]; break; }
    const u = b[0] > a[0] ? (t - a[0]) / (b[0] - a[0]) : 0;
    for (let c = 0; c < 3; c++) out[i * 4 + c] = Math.round(lerp(a[1][c], b[1][c], smooth(u)));
    out[i * 4 + 3] = 255;
  }
  return out;
}
const WARM_WHITE = [255, 244, 225];
const FIXED_MAPS = [
  { name: 'feu', cyclic: false, stops: [[0, [0, 0, 0]], [0.3, [150, 10, 0]], [0.6, [255, 110, 0]], [0.85, [255, 220, 60]], [1, WARM_WHITE]] },
  { name: 'glace', cyclic: false, stops: [[0, [0, 0, 0]], [0.35, [10, 30, 140]], [0.7, [40, 200, 255]], [1, [235, 250, 255]]] },
  { name: 'neon', cyclic: true, cycles: 3, stops: [[0, [0, 0, 0]], [0.25, [255, 20, 160]], [0.5, [20, 240, 255]], [0.75, [255, 240, 40]], [1, [0, 0, 0]]] },
  { name: 'aurore', cyclic: true, cycles: 2, stops: [[0, [0, 0, 0]], [0.3, [20, 200, 110]], [0.55, [40, 230, 210]], [0.8, [150, 60, 220]], [1, [0, 0, 0]]] },
  { name: 'cuivre', cyclic: false, stops: [[0, [0, 0, 0]], [0.35, [90, 40, 15]], [0.7, [210, 120, 50]], [0.9, [255, 200, 110]], [1, WARM_WHITE]] },
  { name: 'acide', cyclic: true, cycles: 4, stops: [[0, [0, 0, 0]], [0.4, [120, 255, 20]], [0.7, [255, 255, 60]], [1, [0, 0, 0]]] }
];
export function tablesFixes() {
  return FIXED_MAPS.map((m) => ({ name: m.name, cyclic: m.cyclic, data: gradient(m.stops, m.cycles || 1) }));
}
// Six from the cover's palette: gradients between its colours, black at 0
// and warm white at 255 to keep a core for the bloom; three of them cyclic.
export function tablesPalette(colors) {
  const cols = (colors && colors.length ? colors : ['#39a0ff', '#ff3ba7', '#ffd23b']).map((h) => { const c = new THREE.Color(h); return [Math.round(c.r * 255), Math.round(c.g * 255), Math.round(c.b * 255)]; });
  const pick = (i) => cols[i % cols.length];
  const out = [];
  out.push({ name: 'palette-a', cyclic: false, data: gradient([[0, [0, 0, 0]], [0.45, pick(0)], [0.8, pick(1)], [1, WARM_WHITE]], 1) });
  out.push({ name: 'palette-b', cyclic: false, data: gradient([[0, [0, 0, 0]], [0.4, pick(1)], [0.75, pick(2)], [1, WARM_WHITE]], 1) });
  out.push({ name: 'palette-c', cyclic: false, data: gradient([[0, [0, 0, 0]], [0.3, pick(2)], [0.6, pick(0)], [0.85, pick(1)], [1, WARM_WHITE]], 1) });
  out.push({ name: 'palette-cycle-a', cyclic: true, data: gradient([[0, [0, 0, 0]], [0.35, pick(0)], [0.65, pick(1)], [1, [0, 0, 0]]], 3) });
  out.push({ name: 'palette-cycle-b', cyclic: true, data: gradient([[0, [0, 0, 0]], [0.3, pick(1)], [0.5, WARM_WHITE], [0.7, pick(2)], [1, [0, 0, 0]]], 2) });
  out.push({ name: 'palette-cycle-c', cyclic: true, data: gradient([[0, [0, 0, 0]], [0.5, pick(2)], [1, [0, 0, 0]]], 4) });
  return out;
}

// =============================================================================
// SHADERS
// =============================================================================
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const fullVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// The feedback pass: one intensity, read back along the flow, guarded,
// dithered on signal. highp on the coordinates: a half-precision uv steps
// across the buffer. The fields are written in centred, aspect-corrected
// coordinates (height 1) and return a displacement per second.
const feedbackFragmentShader = `
  precision highp float;
  uniform sampler2D uPrevious;
  uniform float uAspect;
  uniform float uDt;
  uniform float uTime;
  uniform int uFieldA;
  uniform int uFieldB;
  uniform float uBlend;
  uniform float uSpeed;
  uniform float uSource;      // the drop's SOURCE, on top
  uniform float uDecay;
  uniform float uSeed;
  varying vec2 vUv;
  float hash21(vec2 p) { p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113); p += dot(p, p.yx + 1.9); return fract(p.x * p.y * 7.31); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    i = mod(i, 64.0);
    float a = hash21(i), b = hash21(i + vec2(1.0, 0.0)), c = hash21(i + vec2(0.0, 1.0)), d = hash21(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  // two octaves, unrolled
  float psi(vec2 p) { return 0.65 * vnoise(p) + 0.35 * vnoise(p * 2.03 + 7.1); }
  vec2 field(int id, vec2 p, float t) {
    float r = length(p) + 1e-4;
    vec2 n = p / r;
    vec2 perp = vec2(-n.y, n.x);
    if (id == 0) return perp * 0.30 / (1.0 + r * 3.0);                                  // VORTEX
    if (id == 1) return -n * 0.26 * (1.0 - 0.4 * r) + perp * 0.08;                     // SINK
    if (id == 2) return n * 0.28;                                                       // SOURCE
    if (id == 3) {                                                                      // TWIN
      float a = t * 0.35;
      vec2 c1 = vec2(cos(a), sin(a)) * 0.28, c2 = -c1;
      vec2 d1 = p - c1, d2 = p - c2;
      float r1 = length(d1) + 1e-4, r2 = length(d2) + 1e-4;
      return vec2(-d1.y, d1.x) / r1 * 0.22 / (1.0 + r1 * 6.0) - vec2(-d2.y, d2.x) / r2 * 0.22 / (1.0 + r2 * 6.0);
    }
    if (id == 4) return vec2(sign(sin(p.y * 6.2831853 * 3.0 + 0.3)) * 0.26, 0.0);         // SHEAR
    if (id == 5) return n * 0.22 * sin(r * 22.0 - t * 2.5);                             // RIPPLE
    if (id == 6) {                                                                      // CURL
      vec2 q = p * 2.0 + vec2(t * 0.05, -t * 0.04);
      float e = 0.03;
      float c = psi(q);
      return vec2(psi(q + vec2(0.0, e)) - c, -(psi(q + vec2(e, 0.0)) - c)) / e * 0.09;
    }
    if (id == 7) {                                                                      // GRID
      float k = 6.2831853 * 2.0;
      return vec2(sin(p.x * k) * cos(p.y * k), -cos(p.x * k) * sin(p.y * k)) * 0.24;
    }
    if (id == 8) return (-n * 0.09 + perp * 0.26) * (0.4 + r);                         // SPIRAL
    return n * (r - 0.32) * 0.55;                                                       // LENS
  }
  void main() {
    vec2 uv = vUv;
    vec2 p = vec2((uv.x - 0.5) * uAspect, uv.y - 0.5);
    vec2 v = mix(field(uFieldA, p, uTime), field(uFieldB, p, uTime), uBlend) * uSpeed;
    v += field(2, p, uTime) * uSource;
    // back to uv: x is wider by the aspect
    vec2 src = uv - vec2(v.x / uAspect, v.y) * uDt;
    float prev = texture2D(uPrevious, src).r;
    prev *= uDecay;
    prev -= ${FLOOR_SUB.toFixed(4)};
    prev = max(prev, 0.0);
    prev = min(prev, ${CEILING.toFixed(3)});
    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeed, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    float sig = step(3.5 / 255.0, prev);
    float o = prev + dith * sig;
    gl_FragColor = vec4(o, o, o, 1.0);
  }
`;

// The wave ribbon (preset.js), drawn in GREY: the core at full intensity,
// the halo at half. Nothing coloured ever enters the buffer.
const waveVertexShader = `
  precision highp float;
  attribute float aSide;
  attribute float aAlong;
  uniform float uHalf;
  varying float vSide;
  varying float vAlong;
  void main() {
    vec2 n = vec2(cos(position.z), sin(position.z));
    vec2 p = position.xy + n * uHalf * aSide;
    vSide = aSide; vAlong = aAlong;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 0.0, 1.0);
  }
`;
const waveFragmentShader = `
  precision mediump float;
  uniform float uOpacity;
  uniform float uDots;
  varying float vSide;
  varying float vAlong;
  void main() {
    float r = abs(vSide);
    float core = 1.0 - smoothstep(0.0, 1.0 / ${WAVE_HALO.toFixed(1)}, r);
    float halo = (1.0 - r) * (1.0 - r);
    float bead = mix(1.0, 1.0 - smoothstep(0.30, 0.42, abs(fract(vAlong / ${DOT_PERIOD.toFixed(4)}) - 0.5)), uDots);
    float v = (core * 0.85 + halo * 0.18) * uOpacity * bead;
    gl_FragColor = vec4(v, v, v, 1.0);
  }
`;

// The display: the table, read with stretch and offset; the safe zone on
// the intensity BEFORE the table (a dimmed intensity reads a darker entry,
// never a grey mix); the drop's two-frame inversion; the raw view.
const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  uniform sampler2D uMap;
  uniform float uCyclic;
  uniform float uStretch;
  uniform float uOffset;
  uniform float uInvert;
  uniform float uRaw;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    float i = texture2D(uBuffer, vUv).r * att;
    i = mix(i, 1.0 - i, uInvert);
    if (uRaw > 0.5) { gl_FragColor = vec4(i, i, i, 1.0); return; }
    float u = i * uStretch + uOffset;
    u = uCyclic > 0.5 ? fract(u) : clamp(u, 0.0, 1.0);
    // texel centres: entry 0 at 0.5/256, entry 255 at 255.5/256
    vec3 c = texture2D(uMap, vec2(mix(0.5 / 256.0, 255.5 / 256.0, u), 0.5)).rgb;
    // a cyclic table would paint the empty background with whatever entry
    // the offset lands on: black stays black, the contours flow on the signal
    c *= mix(1.0, smoothstep(0.0, 0.05, i), uCyclic);
    gl_FragColor = vec4(c, 1.0);
  }
`;
const lumFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }
`;

// =============================================================================
export class FlowmapVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const f = urlParam('flowfield');
    this._forcedField = f && FIELD_ID[f] != null ? f : null;
    const m = urlParam('flowmap');
    this._forcedMap = m != null && m !== '' && Number.isFinite(parseInt(m, 10)) ? parseInt(m, 10) : null;
    this._raw = urlParam('flowraw') === '1';
    this._probe = urlParam('flowprobe') === '1';
    this._postProcessing = this._raw
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.55, vignette: 0.25, aberration: 0.001 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    const layout = this._layout;
    this._buffer = new FeedbackBuffer({ scale: layout.scale });

    // --- feedback pass
    this._fb = {
      uPrevious: { value: null }, uAspect: { value: 16 / 9 }, uDt: { value: 1 / 60 }, uTime: { value: 0 },
      uFieldA: { value: 0 }, uFieldB: { value: 0 }, uBlend: { value: 0 }, uSpeed: { value: SPEED_BASE }, uSource: { value: 0 },
      uDecay: { value: DECAY_DEFAULT }, uSeed: { value: 0 }
    };
    this._feedbackScene = new THREE.Scene();
    this._feedbackQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._fb, vertexShader: fullVertexShader, fragmentShader: feedbackFragmentShader, depthTest: false, depthWrite: false }));
    this._feedbackQuad.frustumCulled = false;
    this._feedbackScene.add(this._feedbackQuad);

    // --- injection pass: the waves, grey, additive
    this._injectScene = new THREE.Scene();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._waves = [this._makeWave(), this._makeWave()];
    for (const w of this._waves) this._injectScene.add(w.mesh);

    // --- governor target
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
    this._lumScene = new THREE.Scene();
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumUniforms, vertexShader: fullVertexShader, fragmentShader: lumFragmentShader, depthTest: false, depthWrite: false }));
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);

    // --- the tables
    this._maps = [];
    this._buildMaps(null);

    // --- display quad
    this._disp = Object.assign({
      uBuffer: { value: null }, uMap: { value: this._maps[0].tex }, uCyclic: { value: this._maps[0].cyclic ? 1 : 0 },
      uStretch: { value: 1 }, uOffset: { value: 0 }, uInvert: { value: 0 }, uRaw: { value: this._raw ? 1 : 0 }
    }, { uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather });
    this._displayMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._disp, vertexShader: fullVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false }));
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    // --- state
    this._time = 0; this._paused = false; this._frameCount = 0;
    this._decay = DECAY_DEFAULT; this._decayCeiling = DECAY_DEFAULT; this._governorHot = false; this._lastLum = 0;
    this._fading = false; this._fadeUntil = -1; this._lastTrackId = undefined;
    this._beatS = 0.5; this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null; this._freeBeatAt = 0; this._beatCount = 0; this._beatsSincePhrase = 0;
    this._bands = [0.5, 0.5, 0.5]; this._energy = 0.6;
    this._decor = new Float32Array(WAVE_POINTS); this._level = new Float32Array(WAVE_POINTS);
    let seed = 7; const rnd = () => { seed = (seed * 1103515245 + 12345) % 2147483648; return seed / 2147483648; };
    for (let i = 0; i < WAVE_POINTS; i++) this._decor[i] = 1 + (rnd() * 2 - 1) * DECORRELATION;
    this._gauss = [0, 1, 2].map((b) => { const g = new Float32Array(WAVE_POINTS); for (let i = 0; i < WAVE_POINTS; i++) { const d = (i / (WAVE_POINTS - 1) - BAND_CENTRE[b]) / BAND_SIGMA[b]; g[i] = Math.exp(-0.5 * d * d); } return g; });
    this._fieldFrom = this._forcedField || layout.fields[0]; this._fieldTo = this._fieldFrom; this._fieldT = 1; this._fieldAt = -1e9;
    this._fieldIdx = 0;
    this._waveFrom = 'line'; this._waveTo = 'line'; this._waveT = 1; this._waveAt = -1e9; this._waveIdx = 0; this._wavePhase = 0;
    this._mapIdx = this._forcedMap != null ? ((this._forcedMap % 12) + 12) % 12 : 0; this._setMap(this._mapIdx);
    this._offset = 0; this._offsetTarget = 0; this._offsetSpeed = OFFSET_DRIFT;
    this._beatUntil = -1; this._barUntil = -1; this._sourceUntil = -1; this._invertFrames = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastDropAt = -1e9; this._lastMode = null;
    this._aspect = 0;
  }

  // --- the tables
  _buildMaps(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : null;
    const list = tablesPalette(vivid).concat(tablesFixes());
    if (!this._maps.length) {
      for (const m of list) {
        const tex = new THREE.DataTexture(m.data, MAP_SIZE, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
        tex.colorSpace = THREE.SRGBColorSpace;   // the tables are authored in sRGB; decoded on read
        tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false;
        tex.wrapS = THREE.ClampToEdgeWrapping; tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.needsUpdate = true;
        this._maps.push({ name: m.name, cyclic: m.cyclic, tex });
      }
    } else {
      // the palette tables (the first six) are regenerated in place
      for (let i = 0; i < 6; i++) { this._maps[i].tex.image.data.set(list[i].data); this._maps[i].tex.needsUpdate = true; }
    }
  }
  _setMap(i) {
    this._mapIdx = i;
    const m = this._maps[i];
    this._disp.uMap.value = m.tex; this._disp.uCyclic.value = m.cyclic ? 1 : 0;
  }
  _nextMap() {
    if (this._forcedMap != null) return;
    let i; do { i = Math.floor(Math.random() * this._maps.length); } while (i === this._mapIdx);
    this._setMap(i);
  }

  // --- the waves (preset.js)
  _makeWave() {
    const n = WAVE_POINTS;
    const pos = new Float32Array(n * 2 * 3), side = new Float32Array(n * 2), along = new Float32Array(n * 2);
    const idx = [];
    for (let i = 0; i < n; i++) {
      side[i * 2] = -1; side[i * 2 + 1] = 1;
      along[i * 2] = along[i * 2 + 1] = i / (n - 1);
      if (i < n - 1) { const a = i * 2; idx.push(a, a + 1, a + 3, a, a + 3, a + 2); }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
    geo.setAttribute('aAlong', new THREE.BufferAttribute(along, 1));
    geo.setIndex(idx);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 10);
    const mat = new THREE.ShaderMaterial({ uniforms: { uHalf: { value: 0.004 }, uOpacity: { value: 1 }, uDots: { value: 0 } }, vertexShader: waveVertexShader, fragmentShader: waveFragmentShader, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false; mesh.visible = false;
    return { mesh, geo, mat, pos };
  }
  _curve(dt, beatPhase, boost) {
    const ka = 1 - Math.exp(-dt / ATTACK_TAU), kr = 1 - Math.exp(-dt / RELEASE_TAU);
    const b = this._bands, g = this._gauss, lv = this._level;
    const ph = beatPhase != null ? beatPhase : (this._time / this._beatS) % 1;
    for (let i = 0; i < WAVE_POINTS; i++) {
      const target = (b[0] * g[0][i] + b[1] * g[1][i] + b[2] * g[2][i]) * this._decor[i];
      lv[i] += (target - lv[i]) * (target > lv[i] ? ka : kr);
    }
    const out = this._amp || (this._amp = new Float32Array(WAVE_POINTS));
    for (let i = 0; i < WAVE_POINTS; i++) {
      const x = i / (WAVE_POINTS - 1);
      const wobble = Math.sin((ph + x * 2) * Math.PI * 2) * TEMPO_WOBBLE;
      out[i] = WAVE_AMP * boost * (lv[i] * (1 + wobble) + 0.08 * wobble);
    }
    return out;
  }
  _layWave(wave, type, amp, aspect) {
    const n = WAVE_POINTS, pos = wave.pos;
    const xs = this._wx || (this._wx = new Float32Array(n)), ys = this._wy || (this._wy = new Float32Array(n));
    const half = 0.46 * aspect, ph = this._wavePhase;
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      let x, y;
      if (type === 'circle') { const th = u * Math.PI * 2 + ph, rr = 0.22 + amp[i] * 1.5; x = Math.cos(th) * rr; y = Math.sin(th) * rr; }
      else if (type === 'rose') { const th = u * Math.PI * 2; const rr = (0.12 + amp[i] * 1.6) * Math.abs(Math.cos(2.5 * th + ph)) + 0.06; x = Math.cos(th) * rr; y = Math.sin(th) * rr; }
      else if (type === 'spiral') { const th = u * Math.PI * 2 * 3 + ph; const rr = 0.04 + u * 0.36 + amp[i] * 1.2; x = Math.cos(th) * rr; y = Math.sin(th) * rr; }
      else if (type === 'lissajous') { const th = u * Math.PI * 2; x = Math.sin(3 * th + ph) * (0.30 * aspect) * (0.8 + amp[i] * 2); y = Math.sin(2 * th) * 0.30 * (0.8 + amp[i] * 2); }
      else { x = u * 2 * half - half; y = amp[i] * 2 - WAVE_AMP * 0.5; }   // line, dots
      xs[i] = x; ys[i] = y;
    }
    for (let i = 0; i < n; i++) {
      const j = Math.min(n - 1, i + 1), k = Math.max(0, i - 1);
      const tx = xs[j] - xs[k], ty = ys[j] - ys[k];
      const nAng = Math.atan2(tx, -ty);
      for (let s = 0; s < 2; s++) { const o = (i * 2 + s) * 3; pos[o] = xs[i]; pos[o + 1] = ys[i]; pos[o + 2] = nAng; }
    }
    wave.geo.attributes.position.needsUpdate = true;
  }

  // --- governor (echo.js)
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;
    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) this._decay = Math.max(DECAY_MIN, this._decay - GOVERN_STEP);
      else this._governorHot = false;
    } else if (lum < GOVERN_LOW && this._decay < Math.min(this._decayCeiling, DECAY_MAX)) {
      this._decay = Math.min(Math.min(this._decayCeiling, DECAY_MAX), this._decay + GOVERN_STEP);
    }
  }

  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    if (rebuilt) {
      this._disp.uBuffer.value = this._buffer.read.texture;
      const aspect = this._buffer.width / Math.max(1, this._buffer.height);
      this._ortho.left = -0.5 * aspect; this._ortho.right = 0.5 * aspect; this._ortho.updateProjectionMatrix();
      this._fb.uAspect.value = aspect;
      this._bufAspect = aspect;
      const px = 1 / Math.max(1, this._buffer.height);
      for (const w of this._waves) w.mat.uniforms.uHalf.value = px * 1.1;
    }
    if (this._paused) { this._disp.uBuffer.value = this._buffer.read.texture; return; }
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    this._fb.uPrevious.value = this._buffer.read.texture;
    this._fb.uDecay.value = this._fading ? FADE_DECAY : this._decay;
    this._fb.uSeed.value = (this._frameCount * 13.13) % 64;
    renderer.setRenderTarget(this._buffer.write);
    renderer.autoClear = false;
    renderer.render(this._feedbackScene, this._ortho);
    renderer.render(this._injectScene, this._ortho);
    this._frameCount++;
    if (!this._fading && this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    this._buffer.swap();
    this._disp.uBuffer.value = this._buffer.read.texture;
  }

  _updateDropSchedule(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (let i = 0; i < marks.length; i++) if (marks[i].type === 'drop' && marks[i].score >= 0.7) this._drops.push(marks[i].atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return null;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    const ref = this._lastNowMs != null ? Math.min(this._lastNowMs, now) : now;
    this._lastNowMs = now;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] < ref - 250) this._dropIdx++;
    if (this._dropIdx >= this._drops.length) return null;
    return (this._drops[this._dropIdx] - now) / 1000;
  }

  _nextField() {
    if (this._forcedField) return;
    const fields = this._layout.fields;
    let i; do { i = Math.floor(Math.random() * fields.length); } while (fields[i] === this._fieldTo && fields.length > 1);
    this._fieldFrom = this._fieldTo; this._fieldTo = fields[i]; this._fieldT = 0; this._fieldAt = this._time;
  }
  _nextWave() {
    let i; do { i = Math.floor(Math.random() * WAVES.length); } while (WAVES[i] === this._waveTo && WAVES.length > 1);
    this._waveFrom = this._waveTo; this._waveTo = WAVES[i]; this._waveT = 0; this._waveAt = this._time;
  }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused) {
      // the intensity freezes; the table keeps scrolling, slowly
      this._offset = (this._offset + PAUSE_DRIFT * dt) % 1;
      this._disp.uOffset.value = this._offset;
      return;
    }
    this._time += dt;
    const t = this._time;
    const director = profile.director || {};
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : 0;
    this._beatS = bpm ? 60 / bpm : 0.5;
    const beatS = this._beatS;
    const measured = !!director.hasReliableGrid;
    const bands = director.bands || {};
    const target = measured ? [bands.low || 0, bands.mid || 0, bands.high || 0] : [0.5 + 0.3 * Math.sin(t * 2.1), 0.5 + 0.3 * Math.sin(t * 1.3 + 1), 0.5 + 0.3 * Math.sin(t * 3.1 + 2)];
    const k = Math.min(1, dt / BAND_TAU);
    for (let i = 0; i < 3; i++) this._bands[i] += (target[i] - this._bands[i]) * k;
    const energy = director.intensity != null ? director.intensity : 0.6;
    this._energy += (energy - this._energy) * Math.min(1, dt);

    // --- the track change: a fade to black, never a clear
    if (profile.trackId !== this._lastTrackId) {
      if (this._lastTrackId !== undefined) { this._fading = true; this._fadeUntil = t + TRACK_FADE_S; }
      this._lastTrackId = profile.trackId;
    }
    if (this._fading && t >= this._fadeUntil) this._fading = false;
    if (profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._buildMaps(profile.artworkPalette); }

    // --- clocks
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
    } else if (t - this._freeBeatAt >= beatS) { this._freeBeatAt = t; beatEdge = true; }
    if (beatEdge) this._beatCount++;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
      this._lastBarPhase = profile.barPhase;
    } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (beatEdge) this._beatsSincePhrase++;
    if (!phraseEdge && beatEdge && this._beatsSincePhrase >= 16) phraseEdge = true;
    if (phraseEdge) this._beatsSincePhrase = 0;
    this._lastPhrase = phrase;

    // --- the drop: a new table at once, the intensity inverted for two
    // frames, a strong SOURCE for a bar
    const sUntil = this._updateDropSchedule(profile);
    if (((sUntil != null && sUntil <= 0) || (measured && director.mode === 'drop' && this._lastMode !== 'drop')) && t - this._lastDropAt > 2) {
      this._lastDropAt = t; this._nextMap(); this._invertFrames = DROP_INVERT_FRAMES; this._sourceUntil = t + 4 * beatS;
    }
    this._lastMode = director.mode;
    // --- the rise: the next drop approaching (or the director's build), the
    // offset flows faster and faster
    let rise = 0;
    if (sUntil != null && sUntil > 0 && sUntil < 8 * beatS) rise = 1 - sUntil / (8 * beatS);
    else if (director.mode === 'build') rise = 0.5;
    this._offsetSpeed = lerp(OFFSET_DRIFT, OFFSET_RISE_MAX, rise * rise);
    // --- beat: a notch of the table, the wave doubled
    if (beatEdge) { this._offsetTarget += OFFSET_STEP; this._beatUntil = t + BEAT_BOOST_S; }
    // --- bar: the field's pulse
    if (barEdge) this._barUntil = t + beatS;
    // --- phrase: next field, next wave
    if (phraseEdge) { this._nextField(); this._nextWave(); }

    // --- the offset: drifts, steps on the beat (the step eased over 80 ms)
    this._offsetTarget += this._offsetSpeed * dt;
    this._offset += (this._offsetTarget - this._offset) * Math.min(1, dt / 0.08);
    if (this._offset > 4) { this._offset -= 4; this._offsetTarget -= 4; }
    const cyc = this._maps[this._mapIdx].cyclic;
    const high = clamp01(this._bands[2]);
    if (cyc) { this._disp.uOffset.value = this._offset % 1; this._disp.uStretch.value = lerp(STRETCH_MIN, STRETCH_MAX, high); }
    else { this._disp.uOffset.value = Math.sin(this._offset * Math.PI) * PLAIN_BREATH; this._disp.uStretch.value = lerp(PLAIN_STRETCH_MIN, PLAIN_STRETCH_MAX, high); }
    this._disp.uInvert.value = this._invertFrames > 0 ? 1 : 0;
    if (this._invertFrames > 0) this._invertFrames--;

    // --- the fields
    if (this._fieldT < 1) this._fieldT = Math.min(1, (t - this._fieldAt) / (FIELD_TRANSITION_BEATS * beatS));
    this._fb.uFieldA.value = FIELD_ID[this._fieldFrom]; this._fb.uFieldB.value = FIELD_ID[this._fieldTo]; this._fb.uBlend.value = smooth(this._fieldT);
    const pulse = t < this._barUntil ? BAR_PULSE_GAIN : 1;
    this._fb.uSpeed.value = SPEED_BASE * (0.5 + this._energy) * pulse;
    this._fb.uSource.value = t < this._sourceUntil ? DROP_SOURCE_GAIN * (this._sourceUntil - t) / (4 * beatS) : 0;
    this._fb.uDt.value = dt; this._fb.uTime.value = t % 1000;

    // --- the waves: the curve once, two shapes crossfaded
    const aspect = this._bufAspect || 16 / 9;
    const boost = t < this._beatUntil ? 2 : 1;
    const amp = this._curve(dt, profile.beatPhase, boost);
    this._wavePhase += dt * Math.PI * 2 / (8 * beatS);
    if (this._waveT < 1) this._waveT = Math.min(1, (t - this._waveAt) / (FIELD_TRANSITION_BEATS * beatS));
    const types = [this._waveFrom, this._waveTo], opac = [1 - this._waveT, this._waveT];
    if (types[0] === types[1]) { opac[0] = 0; opac[1] = 1; }
    const gain = lerp(WAVE_GAIN_MIN, WAVE_GAIN_MAX, clamp01(this._energy));
    for (let i = 0; i < 2; i++) {
      const w = this._waves[i];
      const show = opac[i] > 0.01 && !this._fading;
      w.mesh.visible = show;
      if (!show) continue;
      this._layWave(w, types[i], amp, aspect);
      w.mat.uniforms.uOpacity.value = gain * opac[i];
      w.mat.uniforms.uDots.value = types[i] === 'dots' ? 1 : 0;
    }
    if (this._probe && typeof window !== 'undefined') window.__flowmap = this.getProbe();
  }

  getProbe() {
    return { t: this._time, field: this._fieldTo, from: this._fieldFrom, blend: this._fieldT, wave: this._waveTo, map: this._mapIdx, mapName: this._maps[this._mapIdx].name, cyclic: this._maps[this._mapIdx].cyclic, offset: this._disp.uOffset.value, stretch: this._disp.uStretch.value, lum: this._lastLum, decay: this._decay, governor: this._governorHot };
  }

  dispose() {
    if (this._displayMesh) { this._scene.remove(this._displayMesh); this._displayMesh.geometry.dispose(); this._displayMesh.material.dispose(); }
    this._feedbackQuad.geometry.dispose(); this._feedbackQuad.material.dispose();
    for (const w of this._waves) { w.geo.dispose(); w.mat.dispose(); }
    this._lumQuad.geometry.dispose(); this._lumQuad.material.dispose(); this._lumTarget.dispose();
    for (const m of this._maps) m.tex.dispose();
    this._maps = [];
    this._buffer.dispose();
    if (typeof window !== 'undefined' && window.__flowmap) delete window.__flowmap;
  }
}
